import React, { useState, useEffect, useRef, useCallback } from "react";
import logo from "../../images/logo2.svg";
import Popup from "./Popup";
import axios from "axios";
import prescription from "../../images/prescription.png";
import { Link } from "react-router-dom";
import {
  FaUser,
  FaShoppingBag,
  FaSignOutAlt,
  FaShoppingCart,
  FaHeart,
  FaPrescriptionBottleAlt,
} from "react-icons/fa";
import LoginUser from "../../images/LoginUser.png";
import { FaHistory } from "react-icons/fa";
import user from "../../images/user.svg";
import login from "../../images/login.svg";
import cart from "../../images/cart.svg";
import { useCart } from "../../Context/Createcontext";
import { useApp } from "../../Context/AppContext";
import { useWishlist } from "../../Context/WishlistContext";
import { normalizeSearchQuery } from "../../utils/normalizeSearch";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { api_url } from "../../config/config";
import { toProductSlug } from "../../utils/productSlug";

/**
 * SearchDropdown — module scope, not nested inside Navbar. A component
 * defined inside another component's body gets a brand new function
 * identity on every parent re-render, which makes React unmount and
 * remount its DOM instead of patching it — resetting this list's
 * scroll position to the top on every re-render (e.g. every time hovering
 * a row updates highlightedIndex). Keeping it at module scope keeps the
 * DOM node stable across re-renders so manual scrolling isn't clobbered.
 * `custom-scrollbar` class → brand teal scrollbar, defined in global CSS.
 */
const SearchDropdown = ({
  isMobile,
  itemRefs,
  loading,
  error,
  searchResults,
  highlightedIndex,
  highlightSourceRef,
  setHighlightedIndex,
  handlestoresearchproduct,
}) => (
  <div
    className={`absolute top-[calc(100%+8px)] left-0 ${
      isMobile ? "w-[90vw] max-w-[340px]" : "w-full min-w-[300px]"
    } bg-white border border-[#d4ecea] rounded-2xl shadow-2xl z-[200] overflow-hidden`}
  >
    {/* custom-scrollbar → teal branded scrollbar from global CSS */}
    <div className="max-h-[360px] overflow-y-auto custom-scrollbar">
      {loading ? (
        <p className="py-5 text-center text-[13px] text-[#8faba9] font-poppins">
          Searching…
        </p>
      ) : error ? (
        <p className="py-5 text-center text-[13px] text-red-500 font-poppins">
          {error}
        </p>
      ) : searchResults.length > 0 ? (
        searchResults.map((product, i) => (
          <div
            key={i}
            ref={(el) => (itemRefs.current[i] = el)}
            onMouseDown={(e) => {
              e.preventDefault();
              handlestoresearchproduct(product.product_id, product);
            }}
            onMouseEnter={() => {
              highlightSourceRef.current = "mouse";
              setHighlightedIndex(i);
            }}
            className={`flex items-center gap-3 px-4 py-3 border-b border-[#f1f5f5] last:border-b-0 transition-colors duration-150 cursor-pointer ${
              i === highlightedIndex ? "bg-[#eaf4f3]" : "hover:bg-[#eaf4f3]"
            }`}
          >
            <img
              src={
                product?.product_images?.image_url?.primary_image ||
                "/default.svg"
              }
              alt={product?.name || "Product"}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/default.svg";
              }}
              className="w-9 h-9 object-cover rounded-lg border border-[#e2eaea] flex-shrink-0"
            />
            <span className="text-[13px] font-semibold text-[#1e4a46] font-poppins truncate">
              {product?.name}
            </span>
          </div>
        ))
      ) : (
        <p className="py-6 text-center text-[13px] text-[#8faba9] font-poppins">
          No medicines found
        </p>
      )}
    </div>
  </div>
);

// Constant scroll speed (px/second) — a fixed animation duration made long
// offer text visibly zoom by, since the same duration then has to cover a
// much wider strip of repeated content. Measuring the actual rendered
// width and deriving the duration from it keeps the speed constant no
// matter how long the title/subtitle text is.
const MARQUEE_SPEED_PX_PER_SEC = 14;
const MARQUEE_MIN_DURATION_SEC = 30;

/**
 * TickerBar — module scope, not nested inside Navbar, for the same reason
 * SearchDropdown is: a component redefined on every parent render gets a
 * new identity each time, forcing React to remount its DOM (restarting the
 * marquee animation) on every unrelated Navbar re-render.
 */
const TickerBar = ({ tickerOffers }) => {
  const trackRef = useRef(null);
  const [duration, setDuration] = useState(140);

  const items =
    tickerOffers.length > 0
      ? tickerOffers
      : [{ title: "Free Home Delivery" }];

  useEffect(() => {
    const trackWidth = trackRef.current?.offsetWidth || 0;
    if (!trackWidth) return;
    // The keyframes translate by -50%, i.e. half of this track's own
    // width, before looping — that half is the actual scroll distance.
    const distance = trackWidth / 2;
    setDuration(
      Math.max(MARQUEE_MIN_DURATION_SEC, distance / MARQUEE_SPEED_PX_PER_SEC),
    );
  }, [tickerOffers]);

  const content = (
    <>
      {items.map((offer, i) => (
        <React.Fragment key={i}>
          {offer.title}
          {offer.subtitle ? ` — ${offer.subtitle}` : ""}
          <span className="mx-3 w-1 h-1 rounded-full bg-[#ff601e] inline-block" />
        </React.Fragment>
      ))}
    </>
  );

  return (
    <div className="w-full h-7 bg-[#1e4a46] overflow-hidden flex items-center">
      <div
        ref={trackRef}
        className="flex animate-marquee-infinite"
        style={{ animationDuration: `${duration}s` }}
      >
        {Array.from({ length: 24 }).map((_, i) => (
          <span
            key={i}
            className="text-[11px] font-medium text-white/85 tracking-wide flex items-center font-poppins whitespace-nowrap"
          >
            {content}
          </span>
        ))}
      </div>
    </div>
  );
};

const Navbar = () => {
  const { contextConcerns, contextCategories } = useApp();
  const [showConcerns, setShowConcerns] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const navigate = useNavigate();
  const { cartCount, handleLogout } = useCart();
  const { wishlistCount } = useWishlist();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const desktopDropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const [showResults, setShowResults] = useState(false);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Tracks whether the current highlight came from the keyboard or from the
  // mouse hovering a row — auto-scroll should only follow keyboard nav,
  // otherwise it fights the user when they drag the results list's own
  // scrollbar (the cursor still hovers a row mid-drag, re-triggering scroll).
  const highlightSourceRef = useRef("keyboard");
  const searchContainerRef = useRef(null);
  const mobileSearchContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const resultItemRefs = useRef([]);
  const mobileResultItemRefs = useRef([]);

  /* ── auth ── */
  useEffect(() => {
    const checkLogin = () => setIsLoggedIn(!!localStorage.getItem("authToken"));
    checkLogin();
    window.addEventListener("authChanged", checkLogin);
    return () => window.removeEventListener("authChanged", checkLogin);
  }, []);

  /* ── click outside ──
     The results dropdown is position:absolute and renders below its
     parent's own (small) layout box, so a bounding-rect check would treat
     nearly every click inside it as "outside". DOM containment doesn't
     have that problem — the dropdown is still a real descendant of its
     search container regardless of where it visually overflows to. */
  const isInsideRef = (ref, event) =>
    !!ref.current && ref.current.contains(event.target);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown menu
      if (
        desktopDropdownRef.current &&
        !desktopDropdownRef.current.contains(event.target) &&
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(event.target) &&
        !event.target.closest(".dropdown-link")
      ) {
        setIsDropdownOpen(false);
      }

      // Close search results — only when the click is outside BOTH the
      // desktop and mobile search boxes (both stay mounted at once, one
      // just CSS-hidden, so checking either alone would always "miss").
      const insideDesktopSearch = isInsideRef(searchContainerRef, event);
      const insideMobileSearch = isInsideRef(mobileSearchContainerRef, event);
      if (!insideDesktopSearch && !insideMobileSearch) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ── keep the keyboard-highlighted suggestion in view ──
     Both the desktop and mobile SearchDropdown are mounted at once (one is
     just CSS-hidden), so scroll whichever copy is actually visible — the
     hidden one's scrollIntoView is a harmless no-op. */
  useEffect(() => {
    if (highlightedIndex < 0) return;
    if (highlightSourceRef.current === "mouse") return;
    resultItemRefs.current[highlightedIndex]?.scrollIntoView({
      block: "nearest",
    });
    mobileResultItemRefs.current[highlightedIndex]?.scrollIntoView({
      block: "nearest",
    });
  }, [highlightedIndex]);

  /* ── search ── */
  const fetchResults = async (q) => {
    const normalized = normalizeSearchQuery(q);
    if (!normalized) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${api_url}/products-search?query=${encodeURIComponent(normalized)}&instock=true`,
        {
          headers: { "Content-Type": "application/json", "x-auth": "false" },
        },
      );
      let results = res.data.data || [];
      if (Array.isArray(results)) {
        results = results.filter((p) => {
          const matchesName = (p?.name || "")
            .toLowerCase()
            .includes(normalized.toLowerCase());
          // Keep out-of-stock matches visible. Manually added and bulk-
          // uploaded products may not have branch inventory assigned yet;
          // their detail page handles the unavailable state.
          return matchesName;
        });
        setSearchResults(results);
      } else {
        setSearchResults([]);
      }
      setShowResults(true);
    } catch {
      setError("Failed to fetch products. Please try again later.");
      setSearchResults([]);
      setShowResults(true);
    } finally {
      setLoading(false);
    }
  };

  // Debounced so the API is only hit once typing pauses, not on every
  // keystroke — the query itself still updates the input instantly.
  const debouncedQuery = useDebouncedValue(query, 400);
  useEffect(() => {
    fetchResults(debouncedQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const handleInputChange = (e) => {
    // Strip leading spaces — a query that starts with whitespace throws
    // off the search API and shouldn't be typeable in the first place.
    const val = e.target.value.replace(/^\s+/, "");
    setQuery(val);
    setShowResults(true);
    setHighlightedIndex(-1);
    if (!val.trim()) {
      // Clearing should hide results immediately, not after the debounce.
      setSearchResults([]);
      setShowResults(false);
    }
  };

  /* ── Search icon click / Enter key (no suggestion highlighted):
     go to the All Medicines page pre-filtered to this search term, so the
     full set of matches is shown there — not just the dropdown's preview. */
  const handleSearchSubmit = () => {
    const trimmed = normalizeSearchQuery(query);
    if (!trimmed) return;
    setShowResults(false);
    setHighlightedIndex(-1);
    setQuery("");
    navigate("/products", { state: { initialSearch: trimmed } });
  };

  /* ── Keyboard nav: ↑/↓ move through suggestions, Enter picks the
     highlighted one (or resubmits the search if none is highlighted),
     Escape closes the list ── */
  const handleSearchKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSearchSubmit();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      highlightSourceRef.current = "keyboard";
      setHighlightedIndex((prev) => (prev + 1) % searchResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      highlightSourceRef.current = "keyboard";
      setHighlightedIndex(
        (prev) => (prev - 1 + searchResults.length) % searchResults.length,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && searchResults[highlightedIndex]) {
        const product = searchResults[highlightedIndex];
        handlestoresearchproduct(product.product_id, product);
      } else {
        handleSearchSubmit();
      }
    } else if (e.key === "Escape") {
      setShowResults(false);
      setHighlightedIndex(-1);
    }
  };

  /* ── Clear (×): reset the query but keep the input focused/highlighted ── */
  const handleClearSearch = (inputRef) => {
    setQuery("");
    setShowResults(false);
    setSearchResults([]);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handlestoresearchproduct = (product_id, product) => {
    // Close search results and clear query first
    setShowResults(false);
    setQuery("");

    // Then navigate
    navigate(`/products/${toProductSlug(product)}`, {
      state: { product_id },
    });
  };

  /* ── misc handlers ── */
  const toggleDropdown = () => setIsDropdownOpen((p) => !p);
  const togglePopup = () => setPopupOpen((p) => !p);
  const openSidebar = () => setIsSidebarOpen((p) => !p);
  const closeSidebar = () => setIsSidebarOpen(false);

  const handleUploadPrescriptionClick = (e) => {
    if (!isLoggedIn) {
      e.preventDefault();
      // A fixed id means repeated clicks refresh this one toast instead of
      // stacking duplicates, and the plain (non-error) call keeps this an
      // informational nudge rather than an error state.
      toast("Please log in before uploading a prescription.", {
        id: "login-required-prescription",
        icon: "ℹ️",
      });
    }
  };

  const logoutUser = () => {
    localStorage.removeItem("authToken");
    window.dispatchEvent(new Event("authChanged"));
    handleLogout();
    setIsLoggedIn(false);
    toast.success("Logged out successfully");
    navigate("/home");
  };

  const handleDropdownClick = (e, path, state) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    setTimeout(() => navigate(path, { state }), 100);
  };

  const [tickerOffers, setTickerOffers] = useState([]);

  useEffect(() => {
    axios
      .get(`${api_url}/offers/active`)
      .then((res) => setTickerOffers(res.data?.data || []))
      .catch(() => {});
  }, []);

  /**
   * DropdownMenu — account menu shared by desktop + mobile.
   * Pure Tailwind.
   */
  const DropdownMenu = ({ refProp, isMobile }) => {
    const items = [
      {
        icon: <FaUser className="text-[#5fbdb3] text-xs" />,
        label: "My Profile",
        to: "/profilepage",
        state: { scrollTo: "profile" },
        mobileAction: (e) =>
          handleDropdownClick(e, "/profilepage", { scrollTo: "profile" }),
      },
      {
        icon: <FaShoppingBag className="text-[#5fbdb3] text-xs" />,
        label: "My Orders",
        to: "/my-orders",
      },
      {
        icon: <FaPrescriptionBottleAlt className="text-[#5fbdb3] text-xs" />,
        label: "My Prescriptions",
        to: "/my-prescriptions",
      },
      {
        icon: <FaHistory className="text-[#5fbdb3] text-xs" />,
        label: "My Transactions",
        to: "/my-transactions",
      },
      {
        icon: <FaShoppingCart className="text-[#5fbdb3] text-xs" />,
        label: "My Cart",
        to: "/cart",
      },
      {
        icon: <FaHeart className="text-[#5fbdb3] text-xs" />,
        label: "My Wishlist",
        to: "/wishlist",
      },
    ];

    return (
      <div
        ref={refProp}
        className={`absolute ${
          isMobile ? "right-0 -mr-5" : "right-0"
        } top-full mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-[#d4ecea] overflow-hidden z-50`}
      >
        {/* Header strip */}
        <div className="px-4 py-2.5 bg-[#eaf4f3] border-b border-[#d4ecea]">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#3a7a74] font-poppins">
            My Account
          </p>
        </div>

        {items.map((item, i) =>
          isMobile && item.mobileAction ? (
            <div
              key={i}
              className="dropdown-link flex items-center gap-3 px-4 py-2.5 border-b border-[#f1f5f5] hover:bg-[#eaf4f3] cursor-pointer transition-colors duration-150"
              onClick={item.mobileAction}
            >
              <span className="w-4 flex-shrink-0 flex items-center justify-center">
                {item.icon}
              </span>
              <span className="text-[13px] font-medium text-[#374747] font-poppins">
                {item.label}
              </span>
            </div>
          ) : (
            <Link
              key={i}
              to={item.to}
              state={item.state}
              className="dropdown-link flex items-center gap-3 px-4 py-2.5 border-b border-[#f1f5f5] hover:bg-[#eaf4f3] transition-colors duration-150"
              onClick={() => setIsDropdownOpen(false)}
            >
              <span className="w-4 flex-shrink-0 flex items-center justify-center">
                {item.icon}
              </span>
              <span className="text-[13px] font-medium text-[#374747] font-poppins">
                {item.label}
              </span>
            </Link>
          ),
        )}

        {/* Logout */}
        <div
          className="flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 cursor-pointer transition-colors duration-150"
          onClick={logoutUser}
        >
          <FaSignOutAlt className="text-red-400 w-4 flex-shrink-0" />
          <span className="text-[13px] font-medium text-red-500 font-poppins">
            Log Out
          </span>
        </div>
      </div>
    );
  };

  /* ────────────────────────────────────────────────────────────
     RENDER
  ──────────────────────────────────────────────────────────── */
  return (
    <nav>
      {/* ══════════════════════════════════════════════════
          DESKTOP  (lg and above)
      ══════════════════════════════════════════════════ */}
      <div className="hidden lg:block fixed top-0 left-0 right-0 z-[1000]">
        {/* Ticker */}
        <TickerBar tickerOffers={tickerOffers} />

        {/* Main bar */}
        <div className="bg-[#162c2a] h-[72px] flex items-center px-8 gap-4 shadow-[0_4px_24px_rgba(10,23,21,0.4)] border-b border-white/5">
          {/* Logo */}
          <a href="/home" className="flex-shrink-0 flex items-center">
            <img
              src={logo}
              alt="MediSYS"
              className="h-[75px] w-auto object-contain"
            />
          </a>

          {/* Separator */}
          <div className="w-px h-8 bg-white/10 flex-shrink-0" />

          {/* ── Search ── */}
          <div
            className="flex-1 max-w-[380px] relative"
            ref={searchContainerRef}
          >
            <div className="flex items-center gap-2.5 bg-white/[0.08] border border-white/[0.13] rounded-xl h-[44px] px-4 focus-within:bg-white/[0.14] focus-within:border-[#5fbdb3] focus-within:shadow-[0_0_0_3px_rgba(95,189,179,0.18)] transition-all duration-200">
              <button
                type="button"
                onClick={handleSearchSubmit}
                aria-label="Search"
                className="flex-shrink-0 text-white/40 hover:text-white/80 transition-colors cursor-pointer"
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M7.4349 15.0989C9.30942 15.0989 11.022 14.4001 12.3305 13.2517L14.8651 15.59C14.9953 15.7097 15.1599 15.7704 15.3227 15.7704C15.5049 15.7704 15.6853 15.6982 15.8208 15.5522C16.0734 15.2776 16.0576 14.849 15.7821 14.5973L13.2633 12.2714C14.2674 11.0032 14.8694 9.40336 14.8694 7.66432C14.8694 3.56419 11.5348 0.229614 7.4347 0.229614C3.33458 0.229614 0 3.56419 0 7.66432C0 11.7644 3.33458 15.099 7.4347 15.099L7.4349 15.0989ZM7.4349 1.58126C10.7898 1.58126 13.5178 4.30934 13.5178 7.6642C13.5178 11.0191 10.7898 13.7471 7.4349 13.7471C4.08005 13.7471 1.35196 11.0191 1.35196 7.6642C1.35196 4.30934 4.08005 1.58126 7.4349 1.58126Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search medicines, products…"
                className="flex-1 bg-transparent border-none outline-none text-[13px] font-poppins text-white placeholder-white/35 min-w-0"
              />
              {query && (
                <button
                  onClick={() => handleClearSearch(searchInputRef)}
                  className="text-white/35 hover:text-white/70 text-xl leading-none flex-shrink-0 transition-colors"
                >
                  ×
                </button>
              )}
            </div>
            {showResults && (
              <SearchDropdown
                isMobile={false}
                itemRefs={resultItemRefs}
                loading={loading}
                error={error}
                searchResults={searchResults}
                highlightedIndex={highlightedIndex}
                highlightSourceRef={highlightSourceRef}
                setHighlightedIndex={setHighlightedIndex}
                handlestoresearchproduct={handlestoresearchproduct}
              />
            )}
          </div>

          {/* ── Upload Prescription ── */}
          <Link
            to="/my-prescriptions"
            onClick={handleUploadPrescriptionClick}
            className="group flex items-center gap-2 bg-white/[0.08] border border-white/[0.13] rounded-xl h-[44px] px-4 hover:bg-white/[0.16] hover:border-[#5fbdb3] transition-all duration-200 flex-shrink-0"
          >
            <svg width="18" height="18" viewBox="0 0 24 21" fill="none">
              <path
                d="M22.9414 10.4108C22.3555 10.4108 21.8826 10.8836 21.8826 11.4696V16.4317C21.8826 17.7828 20.781 18.8816 19.4327 18.8816H4.56762C3.21926 18.8816 2.11768 17.78 2.11768 16.4317V11.4696C2.11768 10.8836 1.6448 10.4108 1.05884 10.4108C0.472878 10.4108 0 10.8836 0 11.4696V16.4317C0 18.9492 2.05012 20.9992 4.56748 20.9992H19.4297C21.9499 20.9992 24 18.949 24 16.4317V11.4696C24 10.8836 23.5274 10.4108 22.9414 10.4108Z"
                fill="white"
              />
              <path
                d="M13.0578 3.61217L17.2504 7.80482C17.4558 8.01438 17.7275 8.1164 17.9991 8.1164C18.2706 8.1164 18.5423 8.01438 18.7477 7.80619C19.1613 7.39258 19.1613 6.72254 18.7477 6.3103L12.7476 0.310213C12.334 -0.103404 11.6639 -0.103404 11.2517 0.310213L5.25162 6.3103C4.838 6.72392 4.838 7.39395 5.25162 7.80619C5.66524 8.21981 6.33527 8.21981 6.74751 7.80619L10.9402 3.61213V14.4695C10.9402 15.0554 11.413 15.5283 11.999 15.5283C12.585 15.5283 13.0578 15.0554 13.0578 14.4695L13.0578 3.61217Z"
                fill="white"
              />
            </svg>

            <span className="text-[13px] font-medium font-poppins text-white/80 group-hover:text-white transition-colors whitespace-nowrap">
              Upload Prescription
            </span>
          </Link>

          {/* ── Right actions ── */}
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            {/* Wishlist */}
            <div className="relative group">
              <button
                onClick={() => {
                  if (isLoggedIn) navigate("/wishlist");
                  else
                    toast("Please log in before accessing wishlist", {
                      id: "login-required-wishlist",
                      icon: "ℹ️",
                    });
                }}
                className="relative w-[44px] h-[44px] rounded-xl bg-white/[0.08] border border-white/[0.13] flex items-center justify-center text-white/70 hover:bg-white/[0.16] hover:border-[#5fbdb3] hover:text-white transition-all duration-200 hover:-translate-y-px"
              >
                <svg width="20" height="19" viewBox="0 0 24 22" fill="none">
                  <path
                    d="M6.38614 0C4.74261 0 3.09914 0.643735 1.85902 1.94157C-0.621273 4.5372 -0.618074 8.69647 1.85902 11.2938L11.3266 21.2228C11.4128 21.3132 11.5164 21.3852 11.6312 21.4344C11.7461 21.4836 11.8697 21.5089 11.9946 21.5089C12.1195 21.5089 12.2431 21.4836 12.358 21.4344C12.4728 21.3852 12.5765 21.3132 12.6626 21.2228C15.8209 17.9176 18.9815 14.6086 22.1398 11.3034C24.6201 8.70776 24.6201 4.54686 22.1398 1.95119C19.6596 -0.644474 15.5658 -0.644529 13.0856 1.95119L11.9994 3.07576L10.9133 1.94157C9.67315 0.643742 8.02968 0 6.38614 0Z"
                    fill="currentColor"
                  />
                </svg>
                {wishlistCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-[#ff601e] text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-[#162c2a]">
                    {wishlistCount}
                  </span>
                )}
              </button>
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 px-2.5 py-1 text-[10px] font-medium font-poppins text-white bg-[#1e4a46] rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                Wishlist
              </span>
            </div>

            {/* Cart */}
            <div className="relative group">
              <button
                onClick={() => {
                  if (isLoggedIn) navigate("/cart");
                  else
                    toast("Please log in before accessing the cart.", {
                      id: "login-required-cart",
                      icon: "ℹ️",
                    });
                }}
                className="relative w-[44px] h-[44px] rounded-xl bg-white/[0.08] border border-white/[0.13] flex items-center justify-center hover:bg-white/[0.16] hover:border-[#5fbdb3] transition-all duration-200 hover:-translate-y-px"
              >
                <img src={cart} alt="Cart" className="w-[22px] h-[22px]" />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-[#ff601e] text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-[#162c2a]">
                    {cartCount}
                  </span>
                )}
              </button>
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 px-2.5 py-1 text-[10px] font-medium font-poppins text-white bg-[#1e4a46] rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                Cart
              </span>
            </div>

            {/* Account / Login */}
            <div className="relative group">
              {isLoggedIn ? (
                <>
                  <button
                    onClick={(e) => {
                      if (!e.target.closest(".dropdown-link")) {
                        e.stopPropagation();
                        setIsDropdownOpen((p) => !p);
                      }
                    }}
                    className="w-[44px] h-[44px] rounded-xl bg-white/[0.08] border border-white/[0.13] flex items-center justify-center hover:bg-white/[0.16] hover:border-[#5fbdb3] transition-all duration-200 hover:-translate-y-px"
                  >
                    <img src={user} alt="User" className="w-[22px] h-[22px]" />
                  </button>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 px-2.5 py-1 text-[10px] font-medium font-poppins text-white bg-[#1e4a46] rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    My Account
                  </span>
                  {isDropdownOpen && (
                    <DropdownMenu
                      refProp={desktopDropdownRef}
                      isMobile={false}
                    />
                  )}
                </>
              ) : (
                <button
                  onClick={togglePopup}
                  className="flex items-center gap-2 h-[44px] px-5 bg-[#ff601e] hover:bg-[#e55519] rounded-xl text-white text-[13px] font-semibold font-poppins transition-all duration-200 hover:-translate-y-px shadow-[0_3px_14px_rgba(255,96,30,0.35)] hover:shadow-[0_6px_22px_rgba(255,96,30,0.45)] flex-shrink-0"
                >
                  <img
                    src={LoginUser}
                    alt="Login"
                    className="w-4 h-4 brightness-0 invert"
                  />
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          MOBILE  (below lg)
      ══════════════════════════════════════════════════ */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#162c2a] shadow-[0_4px_20px_rgba(10,23,21,0.45)]">
        {/* Ticker */}
        <TickerBar tickerOffers={tickerOffers} />

        {/* Row 1 — Logo + icons + hamburger */}
        <div className="flex items-center justify-between px-4 py-1 border-b border-white/[0.08]">
          <a href="/home" className="flex-shrink-0">
            <img
              src={logo}
              alt="MediSYS"
              className="h-[58px] w-auto object-contain sm:h-[48px]"
            />
          </a>

          <div className="flex items-center gap-2">
            {/* Cart */}
            <button
              onClick={() => {
                if (isLoggedIn) navigate("/cart");
                else
                  toast("Please log in before accessing the cart.", {
                    id: "login-required-cart",
                    icon: "ℹ️",
                  });
              }}
              className="relative w-9 h-9 rounded-xl bg-white/[0.09] border border-white/[0.13] flex items-center justify-center hover:bg-white/[0.18] transition-colors"
            >
              <img src={cart} alt="Cart" className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] bg-[#ff601e] text-white text-[8px] font-bold rounded-full flex items-center justify-center px-0.5 border border-[#162c2a]">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Account */}
            <div className="relative">
              {isLoggedIn ? (
                <>
                  <button
                    onClick={(e) => {
                      if (!e.target.closest(".dropdown-link")) {
                        e.stopPropagation();
                        toggleDropdown();
                      }
                    }}
                    className="w-9 h-9 rounded-xl bg-white/[0.09] border border-white/[0.13] flex items-center justify-center hover:bg-white/[0.18] transition-colors"
                  >
                    <img src={user} alt="User" className="w-5 h-5" />
                  </button>
                  {isDropdownOpen && (
                    <DropdownMenu refProp={mobileDropdownRef} isMobile={true} />
                  )}
                </>
              ) : (
                <button
                  onClick={togglePopup}
                  className="flex items-center gap-1.5 h-9 px-3 bg-[#ff601e] hover:bg-[#e55519] rounded-xl text-white text-[12px] font-semibold font-poppins transition-colors shadow-[0_2px_8px_rgba(255,96,30,0.35)]"
                >
                  <img
                    src={LoginUser}
                    alt="Login"
                    className="w-3 h-3 brightness-0 invert"
                  />
                  Login
                </button>
              )}
            </div>

            {/* Hamburger */}
            <button
              onClick={openSidebar}
              className="w-9 h-9 rounded-xl bg-white/[0.09] border border-white/[0.13] flex items-center justify-center hover:bg-white/[0.18] transition-colors"
            >
              <svg width="18" height="13" viewBox="0 0 18 13" fill="none">
                <rect y="0" width="18" height="2" rx="1" fill="white" />
                <rect y="5.5" width="13" height="2" rx="1" fill="white" />
                <rect y="11" width="9" height="2" rx="1" fill="white" />
              </svg>
            </button>
          </div>
        </div>

        {/* Row 2 — Search + Upload Rx + Quick Order */}
        <div className="flex items-center gap-2 px-4 py-2.5">
          {/* Search */}
          <div className="flex-1 relative" ref={mobileSearchContainerRef}>
            <div className="flex items-center gap-2 bg-white/[0.09] border border-white/[0.13] rounded-xl h-10 px-3 focus-within:bg-white/[0.16] focus-within:border-[#5fbdb3] focus-within:shadow-[0_0_0_2px_rgba(95,189,179,0.2)] transition-all duration-200">
              <button
                type="button"
                onClick={handleSearchSubmit}
                aria-label="Search"
                className="text-white/35 hover:text-white/70 flex-shrink-0 transition-colors cursor-pointer"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M7.4349 15.0989C9.30942 15.0989 11.022 14.4001 12.3305 13.2517L14.8651 15.59C14.9953 15.7097 15.1599 15.7704 15.3227 15.7704C15.5049 15.7704 15.6853 15.6982 15.8208 15.5522C16.0734 15.2776 16.0576 14.849 15.7821 14.5973L13.2633 12.2714C14.2674 11.0032 14.8694 9.40336 14.8694 7.66432C14.8694 3.56419 11.5348 0.229614 7.4347 0.229614C3.33458 0.229614 0 3.56419 0 7.66432C0 11.7644 3.33458 15.099 7.4347 15.099L7.4349 15.0989ZM7.4349 1.58126C10.7898 1.58126 13.5178 4.30934 13.5178 7.6642C13.5178 11.0191 10.7898 13.7471 7.4349 13.7471C4.08005 13.7471 1.35196 11.0191 1.35196 7.6642C1.35196 4.30934 4.08005 1.58126 7.4349 1.58126Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <input
                ref={mobileSearchInputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search medicines…"
                className="flex-1 bg-transparent border-none outline-none text-[13px] font-poppins text-white placeholder-white/35 min-w-0"
              />
              {query && (
                <button
                  onClick={() => handleClearSearch(mobileSearchInputRef)}
                  className="text-white/35 hover:text-white/70 text-xl leading-none flex-shrink-0 transition-colors"
                >
                  ×
                </button>
              )}
            </div>
            {showResults && (
              <SearchDropdown
                isMobile={true}
                itemRefs={mobileResultItemRefs}
                loading={loading}
                error={error}
                searchResults={searchResults}
                highlightedIndex={highlightedIndex}
                highlightSourceRef={highlightSourceRef}
                setHighlightedIndex={setHighlightedIndex}
                handlestoresearchproduct={handlestoresearchproduct}
              />
            )}
          </div>

          {/* Upload Rx icon button */}
          <Link
            to="/my-prescriptions"
            onClick={handleUploadPrescriptionClick}
            className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/[0.09] border border-white/[0.13] flex items-center justify-center hover:bg-white/[0.18] hover:border-[#5fbdb3] transition-all duration-200"
            title="Upload Prescription"
          >
            <svg width="18" height="18" viewBox="0 0 24 21" fill="none">
              <path
                d="M22.9414 10.4108C22.3555 10.4108 21.8826 10.8836 21.8826 11.4696V16.4317C21.8826 17.7828 20.781 18.8816 19.4327 18.8816H4.56762C3.21926 18.8816 2.11768 17.78 2.11768 16.4317V11.4696C2.11768 10.8836 1.6448 10.4108 1.05884 10.4108C0.472878 10.4108 0 10.8836 0 11.4696V16.4317C0 18.9492 2.05012 20.9992 4.56748 20.9992H19.4297C21.9499 20.9992 24 18.949 24 16.4317V11.4696C24 10.8836 23.5274 10.4108 22.9414 10.4108Z"
                fill="white"
              />
              <path
                d="M13.0578 3.61217L17.2504 7.80482C17.4558 8.01438 17.7275 8.1164 17.9991 8.1164C18.2706 8.1164 18.5423 8.01438 18.7477 7.80619C19.1613 7.39258 19.1613 6.72254 18.7477 6.3103L12.7476 0.310213C12.334 -0.103404 11.6639 -0.103404 11.2517 0.310213L5.25162 6.3103C4.838 6.72392 4.838 7.39395 5.25162 7.80619C5.66524 8.21981 6.33527 8.21981 6.74751 7.80619L10.9402 3.61213V14.4695C10.9402 15.0554 11.413 15.5283 11.999 15.5283C12.585 15.5283 13.0578 15.0554 13.0578 14.4695L13.0578 3.61217Z"
                fill="white"
              />
            </svg>
          </Link>

          {/* Quick Order — only when logged in */}
          {isLoggedIn && (
            <Link
              to="/my-orders"
              className="flex-shrink-0 h-10 px-3 bg-[#ff601e] hover:bg-[#e55519] rounded-xl text-white text-[11px] font-semibold font-poppins flex items-center transition-colors whitespace-nowrap shadow-[0_2px_8px_rgba(255,96,30,0.3)]"
            >
              Quick Order
            </Link>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          POPUP
      ══════════════════════════════════════════════════ */}
      {isPopupOpen && <Popup onClose={togglePopup} />}

      {/* ══════════════════════════════════════════════════
          MOBILE SIDEBAR DRAWER
      ══════════════════════════════════════════════════ */}
      {isSidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] lg:hidden"
            onClick={closeSidebar}
          />

          {/* Drawer panel */}
          <div className="fixed top-0 right-0 bottom-0 z-[9999] w-[72%] max-w-[300px] bg-[#162c2a] flex flex-col lg:hidden shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.08]">
              <img
                src={logo}
                alt="MediSYS"
                className="h-9 w-auto object-contain"
              />
              <button
                onClick={closeSidebar}
                className="w-8 h-8 rounded-xl bg-white/[0.09] border border-white/[0.13] flex items-center justify-center hover:bg-white/[0.18] transition-colors"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Nav list — sidebar-scroll = hide-scrollbar utility from global CSS */}
            <ul className="flex-1 overflow-y-auto sidebar-scroll py-3 px-3 space-y-0.5">
              {[
                { label: "Home", to: "/home" },
                { label: "About", to: "/about-us" },
                { label: "Featured", to: "/featured-products" },
                { label: "Brands", to: "/shopbrand" },
              ].map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={closeSidebar}
                    className="flex items-center h-11 px-4 rounded-xl text-[14px] font-medium font-poppins text-white/75 hover:text-white hover:bg-white/[0.09] transition-all duration-150"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}

              {/* Categories accordion — mirrors the desktop SubNavbar's
                  "Medicine Categories" dropdown, which is desktop-only
                  (hidden below lg), so mobile needs its own expand/collapse
                  entry point to browse categories from the sidebar. */}
              <li>
                <button
                  type="button"
                  className="w-full flex items-center justify-between h-11 px-4 rounded-xl text-[14px] font-medium font-poppins text-white/75 hover:text-white hover:bg-white/[0.09] transition-all duration-150"
                  onClick={() => setShowCategories((p) => !p)}
                >
                  <span>Categories</span>
                  <svg
                    className={`transition-transform duration-200 ${showCategories ? "rotate-90" : ""}`}
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M6 12L10 8L6 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {showCategories && (
                  <ul className="ml-4 mt-1 pl-3 border-l border-white/[0.1] space-y-0.5 max-h-[240px] overflow-y-auto sidebar-scroll">
                    {contextCategories && contextCategories.length > 0 ? (
                      contextCategories.map((cat) => (
                        <li key={cat.category_id}>
                          <button
                            className="w-full text-left text-[13px] font-normal font-poppins text-white/55 hover:text-white py-2 px-2 rounded-lg hover:bg-white/[0.07] transition-colors"
                            onClick={() => {
                              navigate(
                                `/shop-by-category/${cat.category_slug || cat.category_id}`,
                                {
                                  state: {
                                    category_id: cat.category_id,
                                    category_name: cat.category_name,
                                  },
                                },
                              );
                              closeSidebar();
                              setShowCategories(false);
                            }}
                          >
                            {cat.category_name}
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="text-[13px] text-white/35 py-2 px-2 font-poppins">
                        Loading…
                      </li>
                    )}
                  </ul>
                )}
              </li>

              {/* Concerns accordion */}
              <li>
                <button
                  type="button"
                  className="w-full flex items-center justify-between h-11 px-4 rounded-xl text-[14px] font-medium font-poppins text-white/75 hover:text-white hover:bg-white/[0.09] transition-all duration-150"
                  onClick={() => setShowConcerns((p) => !p)}
                >
                  <span>Concerns</span>
                  <svg
                    className={`transition-transform duration-200 ${showConcerns ? "rotate-90" : ""}`}
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M6 12L10 8L6 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {showConcerns && (
                  <ul className="ml-4 mt-1 pl-3 border-l border-white/[0.1] space-y-0.5 max-h-[240px] overflow-y-auto sidebar-scroll">
                    {contextConcerns && contextConcerns.length > 0 ? (
                      contextConcerns.map((concern) => (
                        <li key={concern.concern_id}>
                          <button
                            className="w-full text-left text-[13px] font-normal font-poppins text-white/55 hover:text-white py-2 px-2 rounded-lg hover:bg-white/[0.07] transition-colors"
                            onClick={() => {
                              navigate(
                                `/shop-by-concern/${concern.name.toLowerCase().replace(/\s+/g, "-")}`,
                                {
                                  state: {
                                    concern_id: concern.concern_id,
                                    concern_name: concern.name,
                                  },
                                },
                              );
                              closeSidebar();
                              setShowConcerns(false);
                            }}
                          >
                            {concern.name}
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="text-[13px] text-white/35 py-2 px-2 font-poppins">
                        Loading…
                      </li>
                    )}
                  </ul>
                )}
              </li>

              {[
                { label: "All Medicines", to: "/products" },
                { label: "Wellness Guide", to: "/blogs" },
              ].map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={closeSidebar}
                    className="flex items-center h-11 px-4 rounded-xl text-[14px] font-medium font-poppins text-white/75 hover:text-white hover:bg-white/[0.09] transition-all duration-150"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}

              {/* Wishlist */}
              <li>
                <button
                  className="w-full flex items-center justify-between h-11 px-4 rounded-xl text-[14px] font-medium font-poppins text-white/75 hover:text-white hover:bg-white/[0.09] transition-all duration-150"
                  onClick={() => {
                    if (isLoggedIn) {
                      navigate("/wishlist");
                      closeSidebar();
                    } else {
                      toast("Please log in before accessing wishlist", {
                        id: "login-required-wishlist",
                        icon: "ℹ️",
                      });
                      closeSidebar();
                    }
                  }}
                >
                  <span>Wish List</span>
                  {wishlistCount > 0 && (
                    <span className="min-w-[20px] h-5 bg-[#ff601e] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                      {wishlistCount}
                    </span>
                  )}
                </button>
              </li>
            </ul>

            {/* Footer */}
            <div className="px-4 py-4 border-t border-white/[0.08]">
              {isLoggedIn ? (
                <button
                  onClick={() => {
                    logoutUser();
                    closeSidebar();
                  }}
                  className="w-full flex items-center justify-center gap-2 h-10 rounded-xl bg-white/[0.08] border border-white/[0.12] text-[13px] font-medium font-poppins text-white/75 hover:bg-red-500/20 hover:text-red-300 hover:border-red-400/30 transition-all duration-200"
                >
                  <FaSignOutAlt className="text-sm" />
                  Sign Out
                </button>
              ) : (
                <button
                  onClick={() => {
                    togglePopup();
                    closeSidebar();
                  }}
                  className="w-full flex items-center justify-center gap-2 h-10 rounded-xl bg-[#ff601e] hover:bg-[#e55519] text-[13px] font-semibold font-poppins text-white transition-colors shadow-[0_2px_10px_rgba(255,96,30,0.35)]"
                >
                  <img
                    src={LoginUser}
                    alt="Login"
                    className="w-3.5 h-3.5 brightness-0 invert"
                  />
                  Login / Sign Up
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;
